/**
 * RoutePreviewTable.jsx
 *
 * Renders a read-only ArcGIS map preview of all lanes linked to a given Route.
 * The component is designed to be embeddable inside other screens (fillHeight mode)
 * or usable as a standalone widget with its own border and spacing.
 *
 * The preview supports:
 * - Loading route-lane relationships (with server-side filtering for soft-deleted relations)
 * - Resolving each lane into a source/destination location pair
 * - Resolving each location into coordinates
 * - Drawing lane polylines and optional arrowheads
 * - Drawing optional source/destination pins when the number of lanes is reasonable
 * - Highlighting a specific lane (and showing an extra legend entry only when highlighting is active)
 * - Exposing a "busy" signal to the parent to enable a lightweight overlay outside the global loader
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { LoadingContext } from "src/M_Components";
import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import { isFailurePayload } from "src/components/utils/apiResponse";
import EsriGenericMap from "src/components/Maps/EsriGenericMap";

/* =====================================================================================================================
 * Map helpers
 * =================================================================================================================== */

/**
 * Lane polyline color for operationally active relationships.
 * The value is an RGBA array compatible with ArcGIS symbol definitions.
 */
const COLOR_ACTIVE = [0, 0, 0, 255];

/**
 * Lane polyline color for operationally inactive relationships.
 * The value is an RGBA array compatible with ArcGIS symbol definitions.
 */
const COLOR_INACTIVE = [180, 180, 180, 255];

/**
 * Default highlight color used when a current lane is provided but no explicit highlight color is passed.
 * The value is an RGBA array compatible with ArcGIS symbol definitions.
 */
const DEFAULT_HIGHLIGHT_COLOR = [212, 175, 55, 255];

/**
 * Default highlight width used when a current lane is provided but no explicit highlight width is passed.
 * Width is expressed in pixels for ArcGIS line symbols.
 */
const DEFAULT_HIGHLIGHT_WIDTH = 4;

/**
 * Source pin fill color used to represent origin locations.
 * The value is an RGBA array compatible with ArcGIS symbol definitions.
 */
const PIN_COLOR_SOURCE = [20, 120, 60, 255];

/**
 * Destination pin fill color used to represent destination locations.
 * The value is an RGBA array compatible with ArcGIS symbol definitions.
 */
const PIN_COLOR_DESTINATION = [30, 80, 200, 255];

/**
 * Pin fill color used when a location acts as both an origin and a destination across the displayed lanes.
 * The value is an RGBA array compatible with ArcGIS symbol definitions.
 */
const PIN_COLOR_BOTH = [120, 70, 170, 255];

/**
 * Common outline color applied to all pins to improve contrast against the basemap.
 * The value is an RGBA array compatible with ArcGIS symbol definitions.
 */
const PIN_OUTLINE_COLOR = [255, 255, 255, 255];

/**
 * SVG path used to draw a map pin marker as a custom path marker.
 * This path is used by ArcGIS "simple-marker" symbols with style "path".
 */
const PIN_PATH =
  "M16 0C9.4 0 4 5.4 4 12c0 9.4 12 20 12 20s12-10.6 12-20C28 5.4 22.6 0 16 0zM16 17.2c-2.9 0-5.2-2.3-5.2-5.2S13.1 6.8 16 6.8s5.2 2.3 5.2 5.2-2.3 5.2-5.2 5.2z";

/**
 * Attempts to convert a value into a finite number.
 *
 * Purpose:
 * - Ensures IDs and numeric fields can be safely used in numeric comparisons and lookups.
 * - Avoids propagating NaN and Infinity through geometry calculations.
 *
 * Behavior:
 * - Returns null when the input is null/undefined/empty string.
 * - Returns null when the input is not a finite number.
 * - Returns a number for valid finite inputs.
 *
 * @param {any} v Input value
 * @returns {number|null} Finite number or null when not convertible
 */
const toNumberOrNull = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Rounds a number to 6 decimal places.
 *
 * Purpose:
 * - Stabilizes coordinate values and reduces noise caused by excessive decimal precision.
 * - Helps produce stable map layer keys and signatures across renders.
 *
 * Behavior:
 * - Returns the rounded value when the input is finite.
 * - Returns the original input when it is not a finite number.
 *
 * @param {any} n Input number
 * @returns {any} Rounded number or original value when not finite
 */
const round6 = (n) =>
  Number.isFinite(n) ? Math.round(n * 1_000_000) / 1_000_000 : n;

/**
 * Normalizes a label value into a trimmed string.
 *
 * Purpose:
 * - Ensures location labels are safe to display even when missing or non-string.
 * - Avoids undefined/null values in tooltip or attribute fields.
 *
 * @param {any} s Input label
 * @returns {string} Trimmed label string (may be empty)
 */
const safeLabel = (s) => String(s || "").trim();

/**
 * Computes a geographic extent (EPSG:4326) that encloses a list of points.
 *
 * Purpose:
 * - Produces an "extent-like" object suitable for zooming the map view to include all endpoints.
 * - Applies padding so the points are not tight to the viewport edges.
 *
 * Behavior:
 * - Filters invalid coordinates out.
 * - Returns null when no valid points exist.
 * - Adds a minimum extent size when all points collapse into a single coordinate.
 *
 * @param {Array<{lon:number,lat:number}>} pts Input points
 * @param {number} padFactor Padding factor applied to width/height
 * @returns {object|null} ArcGIS extent-like object or null when no valid points exist
 */
const computeExtentFromPoints = (pts, padFactor = 0.18) => {
  // Extract longitude/latitude values and remove invalid coordinates.
  const xs = (pts || []).map((p) => p?.lon).filter(Number.isFinite);
  const ys = (pts || []).map((p) => p?.lat).filter(Number.isFinite);

  // Without at least one valid x and y, an extent cannot be computed.
  if (xs.length === 0 || ys.length === 0) return null;

  // Compute bounds.
  const minLon = Math.min(...xs);
  const maxLon = Math.max(...xs);
  const minLat = Math.min(...ys);
  const maxLat = Math.max(...ys);

  // Ensure a minimum non-zero span so the map can zoom meaningfully.
  const dx = maxLon - minLon || 0.01;
  const dy = maxLat - minLat || 0.01;

  // Compute padding in each dimension.
  const padX = dx * padFactor;
  const padY = dy * padFactor;

  // Return an extent-like object in EPSG:4326 coordinates.
  return {
    xmin: minLon - padX,
    ymin: minLat - padY,
    xmax: maxLon + padX,
    ymax: maxLat + padY,
    spatialReference: { wkid: 4326 },
  };
};

/**
 * Builds an arrow representation between two coordinates as:
 * - A polyline shaft that ends slightly before the destination
 * - A triangular polygon head at the destination
 *
 * Purpose:
 * - Provides directionality for lanes on the map without requiring complex symbology.
 * - Uses simple JSON graphics that can be consumed by a generic ArcGIS layer wrapper.
 *
 * Behavior:
 * - Returns null when coordinates are invalid or when source/destination coincide.
 * - Scales the arrowhead based on the line length within reasonable bounds.
 *
 * @param {{lon:number,lat:number}} src Source coordinate
 * @param {{lon:number,lat:number}} dst Destination coordinate
 * @returns {{polyline:object, polygon:object}|null} Arrow graphics payload or null when invalid
 */
const makeArrowJsonArrow = (src, dst) => {
  // Both endpoints are required.
  if (!src || !dst) return null;

  // Normalize numeric coordinates.
  const aLon = Number(src.lon);
  const aLat = Number(src.lat);
  const bLon = Number(dst.lon);
  const bLat = Number(dst.lat);

  // Reject invalid coordinates early.
  if (![aLon, aLat, bLon, bLat].every(Number.isFinite)) return null;

  // Compute direction vector.
  const dx = bLon - aLon;
  const dy = bLat - aLat;
  const len = Math.hypot(dx, dy);

  // Avoid division by zero and degenerate arrows.
  if (!Number.isFinite(len) || len < 1e-8) return null;

  // Unit vector along the lane.
  const ux = dx / len;
  const uy = dy / len;

  // Arrowhead sizing derived from lane length with a clamp for consistent appearance.
  const headLen = Math.max(0.25, Math.min(1.2, len * 0.1));
  const headWidth = headLen * 0.9;

  // Base of the arrowhead is pulled back from the destination.
  const baseLon = bLon - ux * headLen;
  const baseLat = bLat - uy * headLen;

  // Perpendicular vector used to create the triangle width.
  const px = -uy;
  const py = ux;

  // Arrowhead triangle vertices.
  const leftLon = baseLon + px * headWidth * 0.5;
  const leftLat = baseLat + py * headWidth * 0.5;

  const rightLon = baseLon - px * headWidth * 0.5;
  const rightLat = baseLat - py * headWidth * 0.5;

  // Return graphics payloads for both shaft and head.
  return {
    polyline: {
      type: "polyline",
      epsg: 4326,
      graphics: [
        {
          // The shaft ends at the arrow base to avoid overlapping the head.
          geometry: [[[aLon, aLat], [baseLon, baseLat]]],
          attributes: { id: "lane-arrow-shaft" },
        },
      ],
    },
    polygon: {
      type: "polygon",
      epsg: 4326,
      graphics: [
        {
          // Closed ring representing a triangle.
          geometry: [
            [
              [leftLon, leftLat],
              [bLon, bLat],
              [rightLon, rightLat],
              [leftLon, leftLat],
            ],
          ],
          attributes: { id: "lane-arrow-head" },
        },
      ],
    },
  };
};

/* =====================================================================================================================
 * RoutePreviewTable
 * =================================================================================================================== */

/**
 * RoutePreviewTable renders a map preview of the lanes linked to a route.
 *
 * High-level purpose:
 * - Provides a visual summary of a route by drawing all connected lanes and endpoints.
 *
 * Data sources used:
 * - RouteLaneRels/Route/{routeId}/Lanes: relationship list for the route
 * - Lanes: lane details including endpoint location identifiers
 * - Locations: coordinates and labels for each location identifier
 *
 * Rendering semantics:
 * - Soft-deleted relationships are excluded via "Inactive eq false"
 * - Lane visibility and styling is controlled by the relationship IsActive flag
 *
 * Operational modes:
 * - Uncontrolled mode: relationships are fetched internally
 * - Controlled mode: relationships are injected through linkedLaneRelsOverride and not fetched
 *
 * Highlight mode:
 * - When highlightLaneId is provided, that lane is rendered last using highlightColor/highlightWidth
 * - A "Current lane" legend entry is shown only when highlightLaneId is provided
 *
 * Embed mode:
 * - When fillHeight is true, the component fills the parent container and disables the standalone border
 */
export default function RoutePreviewTable({
  routeId,
  linkedLaneRelsOverride,

  highlightLaneId,
  highlightColor,
  highlightWidth,

  fillHeight,
  onBusyChange,
}) {
  const { t, i18n } = useTranslation();
  const ctx = useContext(LoadingContext);

  /**
   * Base URL for API access, provided by the LoadingContext.
   * This value is required by BufferService to build endpoint URLs.
   */
  const baseUrl = ctx.DEFAULTURL;

  /**
   * Controls a local busy overlay.
   * This state reflects network and geometry resolution activity within this component.
   */
  const [mapBusy, setMapBusy] = useState(false);

  /**
   * Internal list of unique lane relationships for the current route.
   * This state is used only when linkedLaneRelsOverride is not provided.
   *
   * Item shape:
   * - laneId: numeric lane identifier
   * - isActive: operational flag used for styling
   */
  const [linkedLaneRels, setLinkedLaneRels] = useState([]);

  /**
   * Resolved geometry and metadata for lanes that can be drawn.
   * Each entry includes endpoints and labels for later pin rendering and extent fitting.
   *
   * Item shape:
   * - laneId: numeric lane identifier
   * - isActive: operational flag used for styling
   * - srcLocId / dstLocId: endpoint location identifiers
   * - src / dst: coordinate and label payloads
   */
  const [routeLaneGeoms, setRouteLaneGeoms] = useState([]);

  /**
   * Stores the ESRI wrapper API instance when the map component becomes ready.
   * The reference is used for imperative map actions such as zooming to an extent.
   */
  const mapComponentRef = useRef(null);

  /**
   * Tracks the DOM container used for the map.
   * The element is used to validate non-zero dimensions to prevent ArcGIS view initialization failures.
   */
  const mapContainerRef = useRef(null);

  /**
   * Lane cache to reduce repeated lane fetches across resolution cycles.
   * Key: laneId (number)
   * Value: lane DTO returned by the API
   */
  const laneCacheRef = useRef(new Map());

  /**
   * Location cache to reduce repeated location fetches across resolution cycles.
   * Key: locationId (number)
   * Value: normalized location payload { id, lat, lon, name, code }
   */
  const locCacheRef = useRef(new Map());

  /**
   * Maximum number of lanes to resolve and draw.
   * This limit avoids excessive network calls and expensive rendering in the map view.
   */
  const DRAW_MAX_LANES = 500;

  /**
   * API clients used by the map resolution process.
   * The services depend on baseUrl, and are memoized to keep stable instances per baseUrl.
   */
  const lanesBuffer = useMemo(
    () => new BufferService("Lanes", baseUrl),
    [baseUrl]
  );
  const locationsBuffer = useMemo(
    () => new BufferService("Locations", baseUrl),
    [baseUrl]
  );

  /**
   * Builds a route-specific BufferService targeting the route-lane relationship endpoint.
   *
   * Purpose:
   * - Encapsulates endpoint construction for the relationship list.
   *
   * @param {number} rid Route identifier
   * @returns {BufferService} Configured service for route relationship retrieval
   */
  const makeRouteLanesService = useCallback(
    (rid) => new BufferService(`RouteLaneRels/Route/${rid}/Lanes`, baseUrl),
    [baseUrl]
  );

  /**
   * Selects the effective relationships list.
   *
   * Purpose:
   * - Supports a controlled mode where the caller supplies relationships.
   * - Falls back to internal state when no override is provided.
   *
   * @returns {Array<{laneId:number,isActive:boolean}>} Relationship list used by the component
   */
  const effectiveLinkedLaneRels = useMemo(() => {
    if (Array.isArray(linkedLaneRelsOverride)) return linkedLaneRelsOverride;
    return linkedLaneRels;
  }, [linkedLaneRelsOverride, linkedLaneRels]);

  /**
   * Maps the current i18n language into a locale string expected by the ESRI wrapper.
   *
   * Purpose:
   * - Ensures map widgets (search, legend, etc.) display using a consistent locale.
   *
   * @returns {string} ESRI locale code
   */
  const mapLocale = useMemo(() => {
    const lng = String(i18n.language || "pt").toLowerCase();
    if (lng.startsWith("pt")) return "pt-PT";
    if (lng.startsWith("es")) return "es-ES";
    return "en-US";
  }, [i18n.language]);

  /**
   * Widget configuration passed to EsriGenericMap.
   *
   * Notes:
   * - The ArcGIS legend widget is enabled, but this component also renders a custom overlay legend
   *   to match the expected application legend layout.
   */
  const mapWidgets = useMemo(
    () => ({
      search: { position: "top-right" },
      legend: { position: "bottom-right" },
    }),
    []
  );

  /**
   * Map constraints and initial viewpoint.
   * These settings provide a safe starting area and limit zooming out too far.
   */
  const mapConstraints = useMemo(() => ({ minZoom: 2 }), []);
  const defaultMapCenter = useMemo(() => [-9.14, 38.72], []);
  const initialZoom = 3;

  /**
   * Controls rendering complexity for pins and arrowheads.
   * Large selections keep the map legible by avoiding excessive marker density.
   */
  const laneCount = routeLaneGeoms?.length ?? 0;
  const SHOW_PINS_MAX = 160;
  const SHOW_ARROW_HEAD_MAX = 90;

  /**
   * Indicates whether location pins should be shown for the current lane count.
   * Pins are disabled above the threshold to reduce visual clutter and rendering cost.
   */
  const shouldShowPins = laneCount > 0 && laneCount <= SHOW_PINS_MAX;

  /**
   * Indicates whether arrowhead polygons should be shown for the current lane count.
   * Arrowheads are disabled above the threshold to reduce rendering cost.
   */
  const shouldShowArrowHeads =
    laneCount > 0 && laneCount <= SHOW_ARROW_HEAD_MAX;

  /**
   * Stores the ESRI wrapper instance for later use.
   *
   * Purpose:
   * - Enables imperative actions (zooming) after layers are created and the view is ready.
   *
   * @param {any} c ESRI wrapper component API
   */
  const handleMapReady = useCallback((c) => {
    mapComponentRef.current = c;
  }, []);

  /**
   * Propagates map busy state changes to the parent component.
   *
   * Purpose:
   * - Allows a container component to display a local overlay while map content is being prepared.
   */
  useEffect(() => {
    if (typeof onBusyChange === "function") onBusyChange(!!mapBusy);
  }, [mapBusy, onBusyChange]);

  /**
   * Fetches a lane DTO by its identifier, using an in-memory cache.
   *
   * Purpose:
   * - Resolves SourceLocationId and DestinationLocationId for each lane.
   * - Avoids redundant network calls for lanes requested multiple times.
   *
   * Behavior:
   * - Returns null when laneId is not convertible to a finite number.
   * - Returns null when the API call fails or returns a failure payload.
   *
   * @param {number|string} laneId Lane identifier
   * @returns {Promise<object|null>} Lane DTO or null when not available
   */
  const resolveLaneById = useCallback(
    async (laneId) => {
      const idNum = toNumberOrNull(laneId);
      if (!idNum) return null;

      // Cache-first lookup to reduce HTTP traffic.
      const cached = laneCacheRef.current.get(idNum);
      if (cached) return cached;

      try {
        const res = await lanesBuffer.getId(idNum);

        // Failure payloads are treated as a non-resolvable lane.
        if (isFailurePayload(res)) return null;

        laneCacheRef.current.set(idNum, res);
        return res;
      } catch {
        // Network errors are intentionally swallowed to keep the map resilient.
        return null;
      }
    },
    [lanesBuffer]
  );

  /**
   * Fetches a location DTO by its identifier, normalizes it, and caches it.
   *
   * Purpose:
   * - Resolves coordinates and labels required to place pins and compute extents.
   * - Enforces numeric coordinate validity before accepting a location.
   *
   * Behavior:
   * - Returns null when locId is not convertible to a finite number.
   * - Returns null when the API call fails or returns a failure payload.
   * - Returns null when coordinates are missing or non-numeric.
   *
   * @param {number|string} locId Location identifier
   * @returns {Promise<object|null>} Normalized location payload or null when not available
   */
  const resolveLocationById = useCallback(
    async (locId) => {
      const idNum = toNumberOrNull(locId);
      if (!idNum) return null;

      // Cache-first lookup to reduce HTTP traffic.
      const cached = locCacheRef.current.get(idNum);
      if (cached) return cached;

      try {
        const res = await locationsBuffer.getId(idNum);
        if (isFailurePayload(res)) return null;

        // Normalize and validate coordinates.
        const lat = toNumberOrNull(res?.Latitude);
        const lon = toNumberOrNull(res?.Longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

        // Normalize label fields to safe, trimmed strings.
        const out = {
          id: idNum,
          lat: round6(Number(lat)),
          lon: round6(Number(lon)),
          name: safeLabel(res?.Name),
          code: safeLabel(res?.Code),
        };

        locCacheRef.current.set(idNum, out);
        return out;
      } catch {
        // Network errors are intentionally swallowed to keep the map resilient.
        return null;
      }
    },
    [locationsBuffer]
  );

  /**
   * Validates map container dimensions.
   *
   * Purpose:
   * - Highlights potential layout problems when the container has zero width/height.
   * - ArcGIS views require a measurable container to initialize properly.
   *
   * Notes:
   * - The effect is tied to routeId because the component is typically remounted or updated when route changes.
   * - The warning is intentionally kept as a console message to aid troubleshooting.
   */
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    if ((rect.width || 0) <= 2 || (rect.height || 0) <= 2) {
      // eslint-disable-next-line no-console
      console.warn("RoutePreviewTable map container has zero size:", rect);
    }
  }, [routeId]);

  /**
   * Loads and normalizes route-lane relationships for the provided routeId.
   *
   * Purpose:
   * - Retrieves all linked lanes for a route using server-side filtering for soft-deleted relations.
   * - Produces a de-duplicated list of lane identifiers and their operational active state.
   *
   * Execution flow:
   * - Runs only in uncontrolled mode (no linkedLaneRelsOverride).
   * - Fetches paged results from the relationship endpoint.
   * - Applies "Inactive eq false" filter to exclude soft-deleted relations.
   * - Aggregates lane IDs into a map to ensure uniqueness.
   * - If any relationship is active for a lane, the lane is treated as active for rendering.
   *
   * Cancellation:
   * - Uses a local "cancelled" flag to prevent state updates after unmount.
   */
  useEffect(() => {
    // Controlled mode skips internal relationship fetching.
    if (Array.isArray(linkedLaneRelsOverride)) return;

    // Without required inputs, clear state and exit.
    if (!routeId || !baseUrl) {
      setLinkedLaneRels([]);
      return;
    }

    let cancelled = false;

    (async () => {
      setMapBusy(true);

      try {
        const pageSize = 50;
        let page = 1;

        // Aggregator used to de-duplicate lane IDs and preserve "active if any" semantics.
        const acc = new Map();
        const svc = makeRouteLanesService(routeId);

        // Server-side filter to exclude soft-deleted relationships.
        const filterInactiveFalse = encodeURIComponent("Inactive eq false");

        while (true) {
          const res = await svc.filter(
            `?Page=${page}&PageSize=${pageSize}&Filter=${filterInactiveFalse}`
          );

          // Stop immediately if the component has been unmounted.
          if (cancelled) return;

          // Failure payloads terminate the paging loop defensively.
          if (isFailurePayload(res)) break;

          // Normalize the response into a list of items.
          const items = Array.isArray(res?.Items)
            ? res.Items
            : Array.isArray(res)
              ? res
              : [];

          // No items indicates the end of paging.
          if (!items.length) break;

          // Accumulate lane IDs and their operational status.
          for (const it of items) {
            // Accept multiple key shapes to tolerate different backend DTO conventions.
            const laneId = toNumberOrNull(
              it?.LaneId ?? it?.laneId ?? it?.Id ?? it?.id
            );
            if (!laneId) continue;

            // Normalize isActive into a boolean.
            const isActiveRaw = it?.IsActive ?? it?.isActive;
            const isActive =
              isActiveRaw === true ||
              String(isActiveRaw).toLowerCase() === "true";

            // De-duplicate by laneId; prefer active if any record is active.
            if (!acc.has(laneId)) acc.set(laneId, isActive);
            else if (isActive) acc.set(laneId, true);
          }

          // Determine paging termination.
          // TotalPages is preferred when present; otherwise infer completion from item count.
          const totalPages = Number(
            res?.TotalPages ?? (items.length < pageSize ? page : page + 1)
          );
          if (page >= totalPages) break;

          page += 1;
        }

        if (cancelled) return;

        // Convert the aggregation map into the state shape expected by the rest of the component.
        setLinkedLaneRels(
          Array.from(acc.entries()).map(([laneId, isActive]) => ({
            laneId,
            isActive: !!isActive,
          }))
        );
      } catch {
        // Relationship loading failures result in an empty preview.
        if (!cancelled) setLinkedLaneRels([]);
      } finally {
        if (!cancelled) setMapBusy(false);
      }
    })();

    // Cleanup function avoids state updates after unmount.
    return () => {
      cancelled = true;
    };
  }, [routeId, baseUrl, makeRouteLanesService, linkedLaneRelsOverride]);

  /**
   * Resolves relationships into map-ready geometries.
   *
   * Purpose:
   * - Converts lane IDs into endpoint coordinates by chaining lane and location lookups.
   * - Produces a compact geometry list used to build map layers and pins.
   *
   * Execution flow:
   * - Clears geometry when routeId is missing or no relationships exist.
   * - Enforces a maximum lane count limit to protect performance.
   * - Loads lane DTOs in parallel.
   * - Extracts unique location IDs, then loads location DTOs in parallel.
   * - Builds a laneId -> isActive lookup for styling.
   * - Creates geometry entries only when both endpoints are available.
   *
   * Cancellation:
   * - Uses a local "cancelled" flag to prevent state updates after unmount.
   */
  useEffect(() => {
    if (!routeId || effectiveLinkedLaneRels.length === 0) {
      setRouteLaneGeoms([]);
      return;
    }

    let cancelled = false;

    (async () => {
      setMapBusy(true);

      try {
        // Establish a stable, unique lane ID set capped to the maximum draw limit.
        const uniqueLaneIds = Array.from(
          new Set(
            effectiveLinkedLaneRels
              .map((x) => x.laneId)
              .filter(Number.isFinite)
          )
        ).slice(0, DRAW_MAX_LANES);

        // Resolve all lane DTOs in parallel, discarding failed results.
        const laneDtos = (
          await Promise.all(uniqueLaneIds.map(resolveLaneById))
        ).filter(Boolean);

        // Collect unique endpoint location IDs from the lane DTOs.
        const locIds = new Set();
        for (const ln of laneDtos) {
          const srcId = toNumberOrNull(
            ln?.SourceLocationId ?? ln?.sourceLocationId
          );
          const dstId = toNumberOrNull(
            ln?.DestinationLocationId ?? ln?.destinationLocationId
          );

          if (srcId) locIds.add(srcId);
          if (dstId) locIds.add(dstId);
        }

        // Resolve and cache all locations needed by the lane set.
        await Promise.all(Array.from(locIds).map(resolveLocationById));

        // Build a quick lookup for active state per lane.
        const activeByLane = new Map(
          effectiveLinkedLaneRels
            .filter((x) => uniqueLaneIds.includes(x.laneId))
            .map((x) => [x.laneId, !!x.isActive])
        );

        // Convert lane DTOs into renderable geometries, skipping incomplete records.
        const geoms = [];
        for (const ln of laneDtos) {
          const laneId = toNumberOrNull(ln?.Id ?? ln?.LaneId);
          if (!laneId) continue;

          const srcLocId = toNumberOrNull(
            ln?.SourceLocationId ?? ln?.sourceLocationId
          );
          const dstLocId = toNumberOrNull(
            ln?.DestinationLocationId ?? ln?.destinationLocationId
          );

          const srcLoc = srcLocId ? locCacheRef.current.get(srcLocId) : null;
          const dstLoc = dstLocId ? locCacheRef.current.get(dstLocId) : null;

          // A lane cannot be rendered without both endpoints.
          if (!srcLoc || !dstLoc) continue;

          geoms.push({
            laneId,
            isActive: activeByLane.get(laneId) ?? true,
            srcLocId,
            dstLocId,
            src: {
              lon: srcLoc.lon,
              lat: srcLoc.lat,
              name: srcLoc.name,
              code: srcLoc.code,
            },
            dst: {
              lon: dstLoc.lon,
              lat: dstLoc.lat,
              name: dstLoc.name,
              code: dstLoc.code,
            },
          });
        }

        if (cancelled) return;
        setRouteLaneGeoms(geoms);
      } finally {
        if (!cancelled) setMapBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [routeId, effectiveLinkedLaneRels, resolveLaneById, resolveLocationById]);

  /**
   * Builds ArcGIS layers from resolved lane geometries.
   *
   * Purpose:
   * - Creates a layer list that EsriGenericMap can render:
   *   - One polyline "shaft" per lane
   *   - Optional arrowhead polygon per lane
   *   - Optional point pin layers for source/destination/both
   *
   * Styling rules:
   * - Active lanes use COLOR_ACTIVE; inactive lanes use COLOR_INACTIVE
   * - Highlighted lane uses highlightColor/highlightWidth and is rendered last
   *
   * Notes:
   * - Layer entries include stable URLs and signatures to reduce unnecessary internal rebuilds.
   * - Pins are generated only when the lane count is below a threshold.
   */
  const mapLayers = useMemo(() => {
    const layers = [];
    if (!Array.isArray(routeLaneGeoms) || routeLaneGeoms.length === 0) return layers;

    // Normalize highlight parameters.
    const currentIdNum = toNumberOrNull(highlightLaneId);
    const hiColor = Array.isArray(highlightColor)
      ? highlightColor
      : DEFAULT_HIGHLIGHT_COLOR;
    const hiWidth = Number.isFinite(Number(highlightWidth))
      ? Number(highlightWidth)
      : DEFAULT_HIGHLIGHT_WIDTH;

    /**
     * Renders a single lane into one or two layers:
     * - A polyline layer for the shaft
     * - An optional polygon layer for the arrowhead
     *
     * @param {object} g Lane geometry entry
     * @param {{color?:number[], width?:number, tag?:string}|null} style Optional override style
     */
    const renderLane = (g, style) => {
      const src = g.src;
      const dst = g.dst;
      if (!src || !dst) return;

      // Operational state controls base styling when no override is provided.
      const isActive = g.isActive !== false;

      const laneIdNum = toNumberOrNull(g.laneId);
      if (!laneIdNum) return;

      const lineColor = style?.color ?? (isActive ? COLOR_ACTIVE : COLOR_INACTIVE);
      const width = style?.width ?? 2;

      // Signature provides stable identity for the layer URL and helps caching within map wrappers.
      const sig = `routeLane:${g.laneId}:${src.lon},${src.lat}->${dst.lon},${dst.lat}:${isActive ? "1" : "0"
        }`;

      // Build arrow graphics (polyline + polygon).
      const arrow = makeArrowJsonArrow(src, dst);

      // Shaft layer is always created when arrow polyline data exists.
      if (arrow?.polyline) {
        layers.push({
          title: "Route Lanes (Shaft)",
          type: "json",
          json: arrow.polyline,
          url: `route-lane-shaft:${style?.tag ?? ""}:${sig}`,
          symbol: { type: "simple-line", color: lineColor, width },
        });
      }

      // Arrowhead layer is optional to control render cost at higher lane counts.
      if (shouldShowArrowHeads && arrow?.polygon) {
        layers.push({
          title: "Route Lanes (Head)",
          type: "json",
          json: arrow.polygon,
          url: `route-lane-head:${style?.tag ?? ""}:${sig}`,
          symbol: {
            type: "simple-fill",
            color: lineColor,
            outline: { type: "simple-line", color: lineColor, width: 0 },
          },
        });
      }
    };

    // Render all lanes except the highlighted one so the highlight stays visually dominant.
    for (const g of routeLaneGeoms) {
      const laneIdNum = toNumberOrNull(g?.laneId);
      if (currentIdNum && laneIdNum === currentIdNum) continue;
      renderLane(g, null);
    }

    // Render the highlighted lane last using highlight styling.
    if (currentIdNum) {
      const g = routeLaneGeoms.find(
        (x) => toNumberOrNull(x?.laneId) === currentIdNum
      );
      if (g) {
        renderLane(g, { color: hiColor, width: hiWidth, tag: "CURRENT" });
      }
    }

    // Pin layers are created only when the selection size is within the allowed threshold.
    if (shouldShowPins) {
      // Aggregate pins by location ID to avoid duplicates.
      const pinByLoc = new Map(); // locId -> {kind, lon,lat,name,code}

      /**
       * Registers a location as a source, destination, or both.
       * If a location appears in both roles across lanes, "both" is assigned.
       *
       * @param {number|null} locId Location identifier
       * @param {"source"|"destination"} kind Role for this occurrence
       * @param {{lon:number,lat:number,name:string,code:string}|null} p Normalized location payload
       */
      const mark = (locId, kind, p) => {
        if (!locId || !p) return;

        const existing = pinByLoc.get(locId);
        const nextKind = !existing ? kind : existing.kind === kind ? kind : "both";

        pinByLoc.set(locId, {
          kind: nextKind,
          lon: p.lon,
          lat: p.lat,
          name: p.name,
          code: p.code,
        });
      };

      // Walk lanes and register both endpoints.
      for (const g of routeLaneGeoms) {
        mark(g.srcLocId, "source", g.src);
        mark(g.dstLocId, "destination", g.dst);
      }

      /**
       * Converts pins for a given kind into GeoJSON features.
       *
       * Purpose:
       * - Provides a format consumable by EsriGenericMap when using "geojson" layers.
       * - Duplicates coordinate properties under multiple common attribute names to support popups
       *   or generic map tooling that expects specific keys.
       *
       * @param {"source"|"destination"|"both"} kind Pin kind to export
       * @returns {Array<object>} GeoJSON feature list
       */
      const toFeatures = (kind) => {
        const features = [];
        for (const [locId, v] of pinByLoc.entries()) {
          if (v.kind !== kind) continue;

          features.push({
            type: "Feature",
            geometry: { type: "Point", coordinates: [v.lon, v.lat] },
            properties: {
              id: `loc-${locId}`,
              locationId: locId,
              kind: v.kind,
              name: v.name,
              code: v.code,

              // Redundant coordinate aliases to maximize compatibility with generic consumers.
              lon: v.lon,
              lat: v.lat,
              longitude: v.lon,
              latitude: v.lat,
              Lon: v.lon,
              Lat: v.lat,
              Longitude: v.lon,
              Latitude: v.lat,
              x: v.lon,
              y: v.lat,
            },
          });
        }
        return features;
      };

      /**
       * Pushes a pin layer into the map layer list when there are features to render.
       *
       * @param {string} title Layer title for the pin category
       * @param {Array<object>} features GeoJSON features to render
       * @param {number[]} color RGBA fill color for the pin symbol
       */
      const pushPins = (title, features, color) => {
        if (!features.length) return;

        layers.push({
          title,
          type: "geojson",
          geojson: { type: "FeatureCollection", features },
          symbol: {
            type: "simple-marker",
            style: "path",
            path: PIN_PATH,
            size: 26,
            color,
            outline: { type: "simple-line", width: 1.8, color: PIN_OUTLINE_COLOR },
          },
        });
      };

      // Create the three optional pin layers.
      pushPins("Route Pins (Source)", toFeatures("source"), PIN_COLOR_SOURCE);
      pushPins(
        "Route Pins (Destination)",
        toFeatures("destination"),
        PIN_COLOR_DESTINATION
      );
      pushPins("Route Pins (Both)", toFeatures("both"), PIN_COLOR_BOTH);
    }

    return layers;
  }, [
    routeLaneGeoms,
    shouldShowPins,
    shouldShowArrowHeads,
    highlightLaneId,
    highlightColor,
    highlightWidth,
  ]);

  /**
   * Fits the map extent to the current lane endpoints.
   *
   * Purpose:
   * - Provides an automatic zoom-to-content experience after lane geometry is available.
   *
   * Safety limits:
   * - Skips extent fitting for extremely large geometry lists to avoid heavy operations.
   *
   * Behavior:
   * - Computes an extent around all lane endpoints and applies padding.
   * - Invokes an imperative map action when supported by the ESRI wrapper.
   */
  useEffect(() => {
    if (!routeLaneGeoms?.length) return;

    // Defensive limit to avoid heavy map operations for extremely large sets.
    if (routeLaneGeoms.length > 800) return;

    // Collect all endpoints into a single point list.
    const pts = [];
    for (const g of routeLaneGeoms) {
      if (g?.src) pts.push({ lon: g.src.lon, lat: g.src.lat });
      if (g?.dst) pts.push({ lon: g.dst.lon, lat: g.dst.lat });
    }

    // Compute a padded extent and apply it to the map view.
    const extent = computeExtentFromPoints(pts, 0.22);
    if (!extent) return;

    try {
      const api = mapComponentRef.current;
      if (typeof api?.mapAction === "function") {
        api.mapAction("zoomToExtent", { extent, padding: 40 });
      }
    } catch {
      // Extent fitting is optional; failures are intentionally ignored.
    }
  }, [routeLaneGeoms]);

  /**
   * Placeholder rendering when a route has not been selected.
   * The component requires routeId to fetch relationships or use the override list meaningfully.
   */
  if (!routeId) {
    return (
      <div style={{ padding: "20px" }}>{t("SelectRoutePreviewLanes.")}</div>
    );
  }

  /**
   * Layout configuration for embedded versus standalone rendering.
   * - fillHeight mode uses 100% height and avoids external margin.
   * - standalone mode adds a top margin and uses a fixed height to provide a consistent preview size.
   */
  const outerStyle = fillHeight ? { height: "100%" } : { marginTop: "20px" };
  const containerHeight = fillHeight ? "100%" : 600;

  /**
   * Legend labels resolved through i18n.
   * The translation keys are expected to exist, and the i18n library provides fallback behavior.
   */
  const legendOriginLabel = t("Origin");
  const legendDestinationLabel = t("Destination");
  const legendBothLabel = t("Origin&Destination");
  const legendLaneActiveLabel = t("LaneActive");
  const legendLaneInactiveLabel = t("LaneInactive");
  const legendLaneCurrentLabel = t("LaneCurrent");

  /**
   * Determines whether a "current lane" legend entry should be displayed.
   * The entry is shown only when highlightLaneId is provided and convertible to a finite number.
   */
  const currentIdNumForLegend = toNumberOrNull(highlightLaneId);
  const showCurrentLaneLegend = !!currentIdNumForLegend;

  /**
   * Determines the highlight color for legend rendering.
   * Only the RGB channels are used for CSS; alpha is ignored in the legend line preview.
   */
  const hiColorForLegend = Array.isArray(highlightColor)
    ? highlightColor
    : DEFAULT_HIGHLIGHT_COLOR;

  const hiColorCss = `rgb(${hiColorForLegend[0]}, ${hiColorForLegend[1]}, ${hiColorForLegend[2]})`;

  return (
    <div style={outerStyle}>
      <div
        ref={mapContainerRef}
        style={{
          width: "100%",
          height: containerHeight,
          overflow: "hidden",
          position: "relative",
          boxSizing: "border-box",
          background: "var(--color-white)",

          // Standalone mode draws its own border; embedded mode delegates visual framing to the parent.
          border: fillHeight ? "none" : "2px solid var(--color-red-dark)",
          borderRadius: fillHeight ? 0 : 10,
        }}
      >
        <EsriGenericMap
          // Key includes routeId and highlight state to ensure map wrapper updates when key parameters change.
          key={`route-preview-map-${routeId}-${toNumberOrNull(highlightLaneId) ?? "none"}`}
          mode="view"
          locale={mapLocale}
          widgets={mapWidgets}
          layers={Array.isArray(mapLayers) ? mapLayers : []}
          center={defaultMapCenter}
          zoom={initialZoom}
          constraints={mapConstraints}
          onComponentReady={handleMapReady}
          showLocations={false}
          locations={[]}
          enableLocationPick={false}
          configDebounceMs={240}
        />

        {/* Local overlay indicating that network and geometry resolution operations are in progress. */}
        {mapBusy && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              background: "rgba(255,255,255,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              fontSize: 12,
              color: "rgba(0,0,0,0.70)",
              fontWeight: 600,
            }}
            aria-label={t("Loading")}
          >
            {t("Loading")}...
          </div>
        )}

        {/* Custom legend overlay matching the expected UI layout and labels. */}
        <div
          style={{
            position: "absolute",
            right: 12,
            bottom: 12,
            zIndex: 6,
            background: "rgba(255,255,255,0.92)",
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 10,
            padding: "10px 12px",
            boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
            backdropFilter: "blur(2px)",
            maxWidth: 260,
            pointerEvents: "none",
          }}
          aria-label={t("Legend")}
        >
          {/* Legend header */}
          <div
            style={{
              fontWeight: 700,
              fontSize: 12,
              marginBottom: 8,
              color: "var(--color-red-dark)",
              textAlign: "right",
            }}
          >
            {t("Legend")}
          </div>

          {/* Legend rows are aligned to the right to match the expected application styling. */}
          <div
            style={{
              display: "grid",
              gap: 8,
              fontSize: 12,
              textAlign: "right",
            }}
          >
            {/* Origin pin */}
            <div
              style={{
                display: "flex",
                flexDirection: "row-reverse",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 999,
                  background: "rgb(20, 120, 60)",
                  border: "2px solid white",
                }}
              />
              <span>{legendOriginLabel}</span>
            </div>

            {/* Destination pin */}
            <div
              style={{
                display: "flex",
                flexDirection: "row-reverse",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 999,
                  background: "rgb(30, 80, 200)",
                  border: "2px solid white",
                }}
              />
              <span>{legendDestinationLabel}</span>
            </div>

            {/* Both origin and destination pin */}
            <div
              style={{
                display: "flex",
                flexDirection: "row-reverse",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 999,
                  background: "rgb(120, 70, 170)",
                  border: "2px solid white",
                }}
              />
              <span>{legendBothLabel}</span>
            </div>

            {/* Active lane line */}
            <div
              style={{
                display: "flex",
                flexDirection: "row-reverse",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 0,
                  borderTop: "2px solid rgb(0,0,0)",
                }}
              />
              <span>{legendLaneActiveLabel}</span>
            </div>

            {/* Inactive lane line */}
            <div
              style={{
                display: "flex",
                flexDirection: "row-reverse",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 0,
                  borderTop: "2px solid rgb(180,180,180)",
                }}
              />
              <span>{legendLaneInactiveLabel}</span>
            </div>

            {/* Current lane line, shown only when a highlight lane ID is provided */}
            {showCurrentLaneLegend && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row-reverse",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span
                  style={{
                    width: 22,
                    height: 0,
                    borderTop: `2px solid ${hiColorCss}`,
                  }}
                />
                <span>{legendLaneCurrentLabel}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Runtime prop validation for RoutePreviewTable.
 *
 * Purpose:
 * - Documents the supported input shape.
 * - Provides runtime warnings in development builds when props are incorrect.
 */
RoutePreviewTable.propTypes = {
  /**
   * Route identifier used to load relationships and render a preview.
   * When omitted, a placeholder message is rendered.
   */
  routeId: PropTypes.number,

  /**
   * Optional controlled relationship list.
   * When provided, internal relationship fetching is skipped.
   */
  linkedLaneRelsOverride: PropTypes.arrayOf(
    PropTypes.shape({
      laneId: PropTypes.number.isRequired,
      isActive: PropTypes.bool,
    })
  ),

  /**
   * Lane identifier to highlight in the rendered preview.
   * When provided, the lane is drawn on top and a legend entry is displayed.
   */
  highlightLaneId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * RGBA color array used for highlight styling.
   * When omitted, DEFAULT_HIGHLIGHT_COLOR is used.
   */
  highlightColor: PropTypes.array,

  /**
   * Width used for highlight styling.
   * When omitted, DEFAULT_HIGHLIGHT_WIDTH is used.
   */
  highlightWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * When true, the map container fills its parent height and does not draw a standalone frame.
   */
  fillHeight: PropTypes.bool,

  /**
   * Optional callback that receives true/false when the component is busy preparing map content.
   */
  onBusyChange: PropTypes.func,
};
